/**
 * Portal home ("/") — the cross-module front page. Two agent streams exist
 * (Agent 1: YouTube audience intelligence; Agent 2: media/public/elite
 * salience), and this page tells the combined story neither module tells
 * alone:
 *
 *   1. Pulse tiles — the headline number from each side, terminal-register.
 *   2. Cross-signal chart — a wealth-tax salience line (agenda-switchable:
 *      media / search / public / elite) overlaid with ONE bar per Gary upload,
 *      each sized by that video's reach (views / likes / comments). The product
 *      thesis made visible: does a big video move the conversation? Per-video
 *      TOTALS (not daily deltas) mean the bars span the full upload history, so
 *      a 6M / 1Y / All range control actually has data to show.
 *   3. Cut-through moments — media spikes that landed near a Gary upload,
 *      joined to that video's audience response (sentiment split).
 *   4. Portal news — a merged feed of uploads / digest versions / spikes /
 *      influence findings, newest first.
 *   5. Module cards — jump-off summaries for each agent.
 *
 * Honest-labelling discipline carries over from the salience module: the
 * overlay invites an eyeball correlation, it does not claim causation; agendas
 * are switched between, never blended.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, apiSalience } from "../lib/api";
import type {
  SalienceInfluence,
  SalienceOverviewTerm,
  SalienceSpike,
  Video,
} from "../lib/api";
import { Panel } from "../components/salience/Panel";
import { Sparkline } from "../components/salience/Sparkline";
import { Segmented } from "../components/charts/Segmented";
import { WindowNav } from "../components/charts/WindowNav";
import { useTimeWindow } from "../components/charts/useTimeWindow";
import { useGeoToggle } from "../components/charts/useGeoToggle";
import { SmoothToggle } from "../components/charts/SmoothToggle";
import { FacetBars, SENTIMENT_LABELS } from "../components/FacetBars";
import {
  DIRECTION_ARROW,
  DIRECTION_CLASS,
  direction,
  bucketTs,
  dayMonth,
  dayMonthYear,
  formatAgendaValue,
  formatHeadlineValue,
  formatHeadlineVelocity,
  GEO_COLOURS,
  GEO_LABELS,
  geosForAgenda,
  makeTimeTickFormatter,
  metricValue,
  rollingMean,
  shortDate,
  termHeadline,
  windowDate,
  type Headline,
} from "../components/salience/format";

const SMOOTH_WINDOW_DAYS = 7;

const DAY_MS = 86_400_000;
const FOCUS_TERM = "wealth_tax";
const BAR_COLOUR = "#c084fc"; // per-upload reach bars — kept distinct from the
// geo line colours (worldwide blue / UK green / US amber)
const UPLOAD_COLOUR = "#aab2ff";

/**
 * Which agenda of the focus term drives the salience line. Exposed so the
 * viewer can test Gary's reach against DIFFERENT salience measures — does a
 * big-view video move the MEDIA conversation? the SEARCH interest? — not just
 * media. Agendas are never blended; you switch between them (plan §1.2).
 */
type FocusAgenda = "media" | "search" | "public" | "elite";
const FOCUS_AGENDAS: { key: FocusAgenda; label: string }[] = [
  { key: "media", label: "Media" },
  { key: "search", label: "Search" },
  { key: "public", label: "Public" },
  { key: "elite", label: "Elite" },
];

/**
 * Per-upload reach metric. Each Gary upload is drawn as ONE bar at its publish
 * date, height = that video's TOTAL count (from overview.video_stats). Unlike
 * the old daily-inflow deltas — which only existed for the last handful of
 * recently-snapshotted uploads (~weeks) — totals exist for EVERY tracked video,
 * so the bars span the whole upload history. Views is the default: the
 * widest-reach signal, and the one that best reads as "how big was this video".
 * The three sit on very different scales (views ≫ likes ≫ comments), so the
 * chart shows one at a time on its own auto-scaled right axis.
 */
type ReachMetric = "views" | "likes" | "comments";
const REACH_METRICS: {
  key: ReachMetric;
  label: string;
  field: "v" | "l" | "c";
}[] = [
  { key: "views", label: "Views", field: "v" },
  { key: "likes", label: "Likes", field: "l" },
  { key: "comments", label: "Comments", field: "c" },
];

/** How much upload history to show against the salience line. */
type RangeKey = "6m" | "1y" | "all";
const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: "6m", label: "6M", days: 183 },
  { key: "1y", label: "1Y", days: 365 },
  { key: "all", label: "All", days: null },
];

const REACH_LABEL: Record<ReachMetric, string> = {
  views: "Views",
  likes: "Likes",
  comments: "Comments",
};

/** Agenda-aware phrasing for the chart hint — what the salience line measures. */
const FOCUS_AGENDA_BLURB: Record<FocusAgenda, string> = {
  media: "share of worldwide media coverage (GDELT)",
  search: "search interest (Google Trends index)",
  public: "count of relevant YouTube videos per day",
  elite: "count of relevant UK Parliament items per day",
};

export function PortalHome() {
  /* ---- Agent 1 (YouTube) -------------------------------------------------- */
  const overviewQ = useQuery({
    queryKey: ["overview"],
    queryFn: api.overview,
    retry: (n, err) =>
      !(err instanceof Error && err.message.includes("404")) && n < 1,
  });
  const videosQ = useQuery({ queryKey: ["videos"], queryFn: api.videos });

  /* ---- Agent 2 (Salience) ------------------------------------------------- */
  const salienceQ = useQuery({
    queryKey: ["salience", "overview"],
    queryFn: apiSalience.overview,
    retry: (n, err) =>
      !(err instanceof Error && err.message.includes("404")) && n < 1,
  });
  const eventsQ = useQuery({
    queryKey: ["salience", "events", "all"],
    queryFn: () => apiSalience.events(),
  });
  // Full focus-term series (term-scoped read, not the slow unscoped scan) — we
  // window it client-side so range presets don't refetch.
  const focusSeriesQ = useQuery({
    queryKey: ["salience", "timeseries", "home", FOCUS_TERM],
    queryFn: () => apiSalience.timeseries({ term: FOCUS_TERM }),
  });

  /* ---- Cross-signal derivations ------------------------------------------- */

  const [reachMetric, setReachMetric] = useState<ReachMetric>("views");
  const [focusAgenda, setFocusAgenda] = useState<FocusAgenda>("media");
  // Opt-in 7-day moving average on the salience lines (default off; the reach
  // bars are never smoothed). Same control + rollingMean as ShareTrendChart.
  const [smooth, setSmooth] = useState(false);

  const videos = videosQ.data ?? [];
  const videoStats = overviewQ.data?.video_stats ?? {};
  const reachField = REACH_METRICS.find((m) => m.key === reachMetric)!.field;

  // Full data bounds (all uploads + the focus-agenda series) feed the shared
  // window hook — the SAME range-preset + drag-zoom + ◀▶ nav logic as the
  // salience ShareTrendChart. Anchor to "now" so the right edge is today even
  // when the latest upload/coverage is older.
  const dataBounds = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of focusSeriesQ.data ?? []) {
      if (p.agenda !== focusAgenda) continue;
      const ts = bucketTs(p.bucketDate);
      if (ts < min) min = ts;
      if (ts > max) max = ts;
    }
    for (const v of videos) {
      if (v.isLivestream || !v.publishedAt) continue;
      const ts = bucketTs(v.publishedAt.slice(0, 10));
      if (ts < min) min = ts;
      if (ts > max) max = ts;
    }
    return min === Infinity ? { min: 0, max: 0 } : { min, max };
  }, [focusSeriesQ.data, focusAgenda, videos]);

  const tw = useTimeWindow({
    ranges: RANGES,
    defaultRange: "6m",
    dataMinTs: dataBounds.min,
    dataMaxTs: dataBounds.max,
    anchor: "now",
  });
  const { winLo, winHi } = tw;

  // Salience lines: the focus term's chosen agenda, one line per geo the agenda
  // exposes (worldwide / UK / US). Value + formatting are agenda-aware — media/
  // search render as a share/index, public/elite as an item count.
  const geos = useMemo(() => {
    const present = new Set(
      (focusSeriesQ.data ?? [])
        .filter((p) => p.agenda === focusAgenda)
        .map((p) => p.geo),
    );
    return geosForAgenda(focusAgenda).filter((g) => present.has(g));
  }, [focusSeriesQ.data, focusAgenda]);

  // Only Worldwide is drawn by default here (keeps the cross-signal chart
  // clean); UK/US start hidden and are toggled on via the legend chips. The Y
  // axis rescales to whichever regions are on (see useGeoToggle).
  const geoToggle = useGeoToggle(geos, {
    defaultHidden: (g) => g !== "worldwide",
  });
  const { visibleGeos } = geoToggle;

  // Full per-geo salience series (unwindowed, ts-sorted) — every present geo
  // gets a column so a toggle just flips whether its <Line>/<Area> renders.
  // Kept unwindowed so smoothing can draw trailing context from just off the
  // left edge; the window is applied when merging chartData below.
  const salienceRowsFull = useMemo(() => {
    const byTs = new Map<number, Record<string, number>>();
    for (const p of focusSeriesQ.data ?? []) {
      if (p.agenda !== focusAgenda || !geos.includes(p.geo)) continue;
      const v = metricValue(p, focusAgenda);
      if (v == null) continue;
      const ts = bucketTs(p.bucketDate);
      const row = byTs.get(ts) ?? { ts };
      row[p.geo] = v;
      byTs.set(ts, row);
    }
    return [...byTs.values()].sort((a, b) => a.ts - b.ts);
  }, [focusSeriesQ.data, focusAgenda, geos]);

  const salienceRendered = useMemo(
    () =>
      smooth
        ? rollingMean(salienceRowsFull, geos, SMOOTH_WINDOW_DAYS)
        : salienceRowsFull,
    [smooth, salienceRowsFull, geos],
  );

  // ts → rendered salience row, so spike dots ride the (possibly smoothed) line.
  const salienceByTs = useMemo(() => {
    const m = new Map<number, Record<string, number>>();
    for (const r of salienceRendered) m.set(r.ts, r);
    return m;
  }, [salienceRendered]);

  const hasSalienceInWindow = useMemo(
    () =>
      salienceRendered.some(
        (r) =>
          r.ts >= winLo &&
          r.ts <= winHi &&
          geos.some((g) => r[g] != null),
      ),
    [salienceRendered, geos, winLo, winHi],
  );

  // One bar per upload at its publish date, sized by the chosen reach metric.
  // Same-day uploads sum; titles collected for the tooltip.
  const uploadBars = useMemo(() => {
    const byTs = new Map<
      number,
      { ts: number; reach: number; titles: string[] }
    >();
    for (const v of videos) {
      if (v.isLivestream || !v.publishedAt) continue;
      const ts = bucketTs(v.publishedAt.slice(0, 10));
      if (ts < winLo || ts > winHi) continue;
      const stat = videoStats[v.videoId];
      const reach = stat ? stat[reachField] : 0;
      const cur = byTs.get(ts) ?? { ts, reach: 0, titles: [] };
      cur.reach += reach;
      if (v.title) cur.titles.push(v.title);
      byTs.set(ts, cur);
    }
    return byTs;
  }, [videos, videoStats, reachField, winLo, winHi]);

  // Merge the windowed per-geo salience columns with the per-upload bars onto
  // one ts-keyed dataset (each geo under its own key, reach under `reach`).
  const chartData = useMemo(() => {
    const byTs = new Map<number, Record<string, number>>();
    for (const row of salienceRendered) {
      if (row.ts < winLo || row.ts > winHi) continue;
      byTs.set(row.ts, { ...row });
    }
    for (const b of uploadBars.values())
      byTs.set(b.ts, { ...(byTs.get(b.ts) ?? { ts: b.ts }), reach: b.reach });
    return [...byTs.values()].sort((a, b) => a.ts - b.ts);
  }, [salienceRendered, uploadBars, winLo, winHi]);

  const domainMin = chartData.length ? chartData[0].ts : 0;
  const domainMax = chartData.length ? chartData[chartData.length - 1].ts : 0;

  const allSpikes = useMemo(
    () =>
      (eventsQ.data ?? []).filter(
        (e): e is SalienceSpike => e.kind === "spike",
      ),
    [eventsQ.data],
  );
  const focusSpikeDots = useMemo(
    () =>
      allSpikes
        .filter(
          (s) =>
            s.termId === FOCUS_TERM &&
            s.agenda === focusAgenda &&
            visibleGeos.includes(s.geo),
        )
        .map((s) => {
          const ts = bucketTs(s.bucketDate);
          const raw = s.value ?? 0;
          return {
            ts,
            value: smooth ? salienceByTs.get(ts)?.[s.geo] ?? raw : raw,
            nearGary: s.nearGaryUpload,
          };
        })
        .filter((s) => s.ts >= domainMin && s.ts <= domainMax),
    [allSpikes, focusAgenda, visibleGeos, smooth, salienceByTs, domainMin, domainMax],
  );

  // Cut-through moments: media spikes near a Gary upload, joined to the
  // upload's audience response. Newest first, capped.
  const cutThroughs = useMemo(
    () =>
      allSpikes
        .filter((s) => s.nearGaryUpload && s.garyUploads.length > 0)
        .sort((a, b) => b.bucketDate.localeCompare(a.bucketDate))
        .slice(0, 3),
    [allSpikes],
  );
  const cutThroughStatsQs = useQueries({
    queries: cutThroughs.map((s) => ({
      queryKey: ["commentStats", s.garyUploads[0].video_id],
      queryFn: () => api.commentStats(s.garyUploads[0].video_id),
      staleTime: 5 * 60_000,
    })),
  });

  /* ---- Tiles + module summaries -------------------------------------------- */

  const ov = overviewQ.data;
  const sentimentTotal = ov ? sumCounts(ov.comments.by_sentiment) : 0;
  const supportivePct =
    ov && sentimentTotal > 0
      ? (100 * (ov.comments.by_sentiment["supportive"] ?? 0)) / sentimentTotal
      : null;

  const salienceTerms = salienceQ.data?.terms ?? {};
  const focusTerm = salienceTerms[FOCUS_TERM];
  const focusHeadline: Headline | null = focusTerm
    ? termHeadline(focusTerm)
    : null;
  const focusLabel = focusTerm?.label ?? "Wealth tax";
  const focusSpark = useMemo(() => {
    if (!focusHeadline) return [];
    return (focusSeriesQ.data ?? [])
      .filter(
        (p) =>
          p.agenda === focusHeadline.agenda && p.geo === focusHeadline.geo,
      )
      .sort((a, b) => a.bucketDate.localeCompare(b.bucketDate))
      .map((p) =>
        focusHeadline.metric === "share" ? p.share : p.volume,
      )
      .filter((v): v is number => v != null);
  }, [focusSeriesQ.data, focusHeadline]);

  const spikes30d = useMemo(() => {
    const lo = new Date(Date.now() - 30 * DAY_MS).toISOString().slice(0, 10);
    return allSpikes.filter((s) => s.bucketDate >= lo);
  }, [allSpikes]);
  const spikes30dNearGary = spikes30d.filter((s) => s.nearGaryUpload).length;

  const newsItems = useMemo(
    () => buildNewsFeed(videos, allSpikes, eventsQ.data ?? [], salienceTerms),
    [videos, allSpikes, eventsQ.data, salienceTerms],
  );

  return (
    <div>
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          garyseconomics · intelligence portal
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
          The whole picture
        </h1>
        <p className="mt-1.5 max-w-2xl text-sm text-ink-muted">
          Two agents, one story:{" "}
          <Link to="/youtube" className="font-medium text-ink underline-offset-2 hover:underline">
            what Gary's audience is saying
          </Link>{" "}
          and{" "}
          <Link to="/salience" className="font-medium text-ink underline-offset-2 hover:underline">
            how loud the wealth-tax conversation is
          </Link>{" "}
          across the media, public and elite agendas — and where the two
          signals touch.
        </p>
      </header>

      {/* 1 — pulse tiles */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <PulseTile
          to="/youtube/comments"
          kicker="YouTube · audience"
          label="comments archived"
          value={ov ? formatNumber(ov.comments.total) : "—"}
          sub={ov ? `${ov.comments.classified_pct}% classified` : "loading…"}
        />
        <PulseTile
          to="/youtube"
          kicker="YouTube · tone"
          label="supportive"
          value={supportivePct != null ? `${supportivePct.toFixed(0)}%` : "—"}
          sub={
            sentimentTotal > 0
              ? `of ${formatNumber(sentimentTotal)} classified`
              : "awaiting classifier"
          }
          accent={supportivePct != null ? "up" : undefined}
        />
        <PulseTile
          to={`/salience/terms/${FOCUS_TERM}`}
          kicker={`Salience · ${focusLabel.toLowerCase()}`}
          label="media coverage share"
          value={focusHeadline ? formatHeadlineValue(focusHeadline) : "—"}
          velocity={focusHeadline}
          spark={focusSpark}
        />
        <PulseTile
          to="/salience"
          kicker="Salience · anomalies"
          label="spikes · 30d"
          value={String(spikes30d.length)}
          sub={
            spikes30d.length > 0
              ? `${spikes30dNearGary} near a Gary upload`
              : "none in window"
          }
        />
      </div>

      {/* 2 — cross-signal chart */}
      <Panel
        title={`${focusLabel} salience × Gary's reach`}
        eyebrow="Cross-signal"
        hint={`${focusLabel} ${FOCUS_AGENDA_BLURB[focusAgenda]} against every Gary upload, each bar sized by its ${reachMetric === "views" ? "view count" : reachMetric}. An eyeball correlation, not a causal claim — does a big video move the conversation? Views, likes and comments sit on very different scales, so pick one at a time.`}
        right={
          <div className="flex flex-col items-end gap-1.5">
            <Segmented
              options={REACH_METRICS}
              value={reachMetric}
              onChange={setReachMetric}
            />
            <Segmented
              options={RANGES}
              value={tw.range}
              onChange={tw.setRange}
              size="sm"
            />
            {tw.showNav && chartData.length > 0 && (
              <WindowNav
                canBack={tw.canBack}
                canFwd={tw.canFwd}
                onBack={() => tw.shift(-1)}
                onFwd={() => tw.shift(1)}
                label={`${windowDate(domainMin || winLo)} – ${windowDate(domainMax || winHi)}`}
                isWindowed={tw.isWindowed}
                onReset={tw.reset}
                resetLabel={tw.isCustom ? "Reset zoom" : "Latest"}
              />
            )}
          </div>
        }
        className="mb-6"
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-terminal-ink-muted">
          <span className="uppercase tracking-wider">Salience line</span>
          <Segmented
            options={FOCUS_AGENDAS}
            value={focusAgenda}
            onChange={setFocusAgenda}
            size="sm"
          />
          {!hasSalienceInWindow && (
            <span className="opacity-70">
              · no data for this measure in range
            </span>
          )}
        </div>
        {focusSeriesQ.isLoading || videosQ.isLoading ? (
          <div className="h-[300px] animate-pulse rounded-lg bg-terminal-panel/50" />
        ) : chartData.length === 0 ? (
          <div className="flex h-[200px] items-center justify-center rounded-lg border border-white/5 bg-terminal-panel/40 text-sm text-terminal-ink-muted">
            Nothing to plot in this window yet — widen the range or wait for the
            next pipeline pass.
          </div>
        ) : (
          <>
            <div className="relative h-[300px] w-full select-none">
              {geoToggle.allHidden && (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-sm text-terminal-ink-muted">
                  All regions hidden — tap a region below to show it.
                </div>
              )}
              <div className="absolute inset-0">
              <ResponsiveContainer>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
                  onMouseDown={(e) => {
                    const ts = Number(e?.activeLabel);
                    if (Number.isFinite(ts)) tw.onDragStart(ts);
                  }}
                  onMouseMove={(e) => {
                    const ts = Number(e?.activeLabel);
                    if (Number.isFinite(ts)) tw.onDragMove(ts);
                  }}
                  onMouseUp={() => tw.onDragEnd()}
                  onMouseLeave={() => tw.onDragEnd()}
                >
                  <defs>
                    {geos.map((g) => (
                      <linearGradient
                        key={g}
                        id={`home-grad-${g}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor={GEO_COLOURS[g]} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={GEO_COLOURS[g]} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={makeTimeTickFormatter(domainMin, domainMax)}
                    stroke="#5a5f9e"
                    tick={{ fontSize: 10, fill: "#9aa0d8" }}
                    tickLine={false}
                    minTickGap={36}
                  />
                  <YAxis
                    yAxisId="sal"
                    tickFormatter={(v) => formatAgendaValue(v, focusAgenda)}
                    stroke="#5a5f9e"
                    tick={{ fontSize: 10, fill: "#9aa0d8" }}
                    tickLine={false}
                    axisLine={false}
                    width={52}
                  />
                  <YAxis
                    yAxisId="reach"
                    orientation="right"
                    tickFormatter={(v) => formatNumber(v)}
                    stroke="#5a5f9e"
                    tick={{ fontSize: 10, fill: "#9aa0d8" }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  {/* Salience lines painted FIRST so the reach bars sit on top
                      of the translucent worldwide fill, not under it. Worldwide
                      is an area (the headline signal), UK/US are plain lines. */}
                  {visibleGeos.map((g) =>
                    g === "worldwide" ? (
                      <Area
                        key={g}
                        yAxisId="sal"
                        type="monotone"
                        dataKey={g}
                        stroke={GEO_COLOURS[g]}
                        strokeWidth={2}
                        fill={`url(#home-grad-${g})`}
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ) : (
                      <Line
                        key={g}
                        yAxisId="sal"
                        type="monotone"
                        dataKey={g}
                        stroke={GEO_COLOURS[g]}
                        strokeWidth={1.75}
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ),
                  )}
                  {/* minPointSize keeps every upload's bar visible even when one
                      viral video pins the linear axis (views span ~50k–4M). */}
                  <Bar
                    yAxisId="reach"
                    dataKey="reach"
                    fill={BAR_COLOUR}
                    fillOpacity={0.9}
                    maxBarSize={22}
                    minPointSize={4}
                    radius={[2, 2, 0, 0]}
                    isAnimationActive={false}
                  />
                  {focusSpikeDots.map((s, i) => (
                    <ReferenceDot
                      key={`sp-${i}`}
                      yAxisId="sal"
                      x={s.ts}
                      y={s.value}
                      r={4}
                      fill={s.nearGary ? "#16c784" : "#ea3943"}
                      stroke="#0f1036"
                      strokeWidth={1.5}
                    />
                  ))}
                  {/* Drag-to-zoom selection highlight. */}
                  {tw.dragStart != null &&
                    tw.dragCur != null &&
                    Math.abs(tw.dragCur - tw.dragStart) > 0 && (
                      <ReferenceArea
                        yAxisId="sal"
                        x1={Math.min(tw.dragStart, tw.dragCur)}
                        x2={Math.max(tw.dragStart, tw.dragCur)}
                        fill="#aab2ff"
                        fillOpacity={0.12}
                        stroke="#aab2ff"
                        strokeOpacity={0.35}
                      />
                    )}
                  <Tooltip
                    content={
                      <CrossSignalTooltip
                        focusAgenda={focusAgenda}
                        geoKeys={visibleGeos}
                        reachLabel={REACH_LABEL[reachMetric]}
                        uploadsByTs={uploadBars}
                      />
                    }
                  />
                </ComposedChart>
              </ResponsiveContainer>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-terminal-ink-muted">
              {geos.map((g) => {
                const on = geoToggle.isVisible(g);
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => geoToggle.toggle(g)}
                    aria-pressed={on}
                    title={
                      on
                        ? `Hide ${GEO_LABELS[g] ?? g}`
                        : `Show ${GEO_LABELS[g] ?? g}`
                    }
                    className={`flex items-center gap-1.5 rounded px-1 transition-colors hover:text-terminal-ink ${
                      on ? "text-terminal-ink-muted" : "text-terminal-ink-muted/40"
                    }`}
                  >
                    <span
                      className="inline-block h-0.5 w-4 rounded"
                      style={{ background: GEO_COLOURS[g], opacity: on ? 1 : 0.3 }}
                    />
                    <span className={on ? "" : "line-through"}>
                      {GEO_LABELS[g] ?? g}
                    </span>
                  </button>
                );
              })}
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: BAR_COLOUR, opacity: 0.7 }} />
                {REACH_LABEL[reachMetric]} per upload
              </span>
              {focusSpikeDots.length > 0 && (
                <>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-up" />
                    Spike (near upload)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-down" />
                    Spike (other)
                  </span>
                </>
              )}
              <SmoothToggle
                smooth={smooth}
                onToggle={() => setSmooth((v) => !v)}
                windowDays={SMOOTH_WINDOW_DAYS}
                className="ml-auto"
              />
              <span className="hidden opacity-70 sm:inline">
                Drag across the chart to zoom
              </span>
            </div>
          </>
        )}

        {/* Cut-through moments — the spike × response join */}
        {cutThroughs.length > 0 && (
          <div className="mt-4 border-t border-white/10 pt-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
              Cut-through moments — spike meets upload
            </div>
            <ul className="space-y-2">
              {cutThroughs.map((s, i) => (
                <CutThroughRow
                  key={`${s.termId}-${s.bucketDate}`}
                  spike={s}
                  termLabel={salienceTerms[s.termId]?.label ?? s.termId}
                  stats={cutThroughStatsQs[i]?.data}
                />
              ))}
            </ul>
          </div>
        )}
      </Panel>

      {/* 3 — news + module cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel
          title="Portal news"
          eyebrow="Latest across both agents"
          hint="New uploads, fresh digest versions, attention spikes and lead-lag findings — one feed, newest first."
        >
          {newsItems.length === 0 ? (
            <div className="text-sm text-terminal-ink-muted">
              Nothing yet — the feed fills as the pipelines run.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {newsItems.map((n, i) => (
                <NewsRow key={`${n.kind}-${i}`} item={n} />
              ))}
            </ul>
          )}
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel
            title="YouTube audience"
            eyebrow="Agent 1"
            right={<ModuleLink to="/youtube" label="Open module" />}
          >
            {ov ? (
              <>
                <div className="mb-3 grid grid-cols-3 gap-2 text-center">
                  <MiniStat value={formatNumber(ov.videos.total)} label="videos" />
                  <MiniStat value={formatNumber(ov.comments.total)} label="comments" />
                  <MiniStat value={formatNumber(ov.engagement.total_views)} label="views" />
                </div>
                <FacetBars
                  counts={ov.comments.by_sentiment}
                  labelMap={SENTIMENT_LABELS}
                  variant="sentiment"
                  emptyMessage="No sentiment tagged yet."
                />
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <FooterLink to="/youtube/comments" label="Comments dashboard" />
                  <FooterLink to="/youtube/themes" label="Themes" />
                </div>
              </>
            ) : (
              <div className="text-sm text-terminal-ink-muted">
                {overviewQ.isLoading
                  ? "Loading channel snapshot…"
                  : "No channel snapshot yet — the hourly ingest computes it."}
              </div>
            )}
          </Panel>

          <Panel
            title="Salience monitor"
            eyebrow="Agent 2"
            right={<ModuleLink to="/salience" label="Open module" />}
          >
            {Object.keys(salienceTerms).length > 0 ? (
              <WatchlistMini terms={salienceTerms} />
            ) : (
              <div className="text-sm text-terminal-ink-muted">
                {salienceQ.isLoading
                  ? "Loading watchlist…"
                  : "No salience snapshot yet — the daily score pass computes it."}
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* 4 — roadmap: future modules (concept previews) */}
      <section className="mt-8">
        <div className="mb-3 flex items-baseline gap-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-ink">
            On the roadmap
          </h2>
          <span className="text-[11px] text-ink-muted">
            Same architecture, new digital colleagues — previews are concept
            mockups, not yet built
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ROADMAP.map((m) => (
            <RoadmapCard key={m.title} module={m} />
          ))}
        </div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Roadmap (future modules)                                                    */
/* -------------------------------------------------------------------------- */

interface RoadmapModule {
  title: string;
  agent: string;
  blurb: string;
  to?: string; // present ⇒ a clickable concept preview exists
}

const ROADMAP: RoadmapModule[] = [
  {
    title: "Discord Monitor",
    agent: "Agent 3",
    blurb:
      "Community intelligence from Gary's Discord — trending topics, questions worth a reply, room sentiment.",
    to: "/preview/discord",
  },
  {
    title: "Inbox Triage",
    agent: "Agent 4",
    blurb:
      "Sorts inbound email by what needs a human, drafts replies for approval. Never sends unattended.",
    to: "/preview/inbox",
  },
  {
    title: "Content Repurposer",
    agent: "Agent 5",
    blurb:
      "Turns each transcript into shorts, threads and a newsletter draft — the long tail of a video, automatically.",
  },
  {
    title: "Press Monitor",
    agent: "Agent 6",
    blurb:
      "Tracks mentions of Gary across media and politics — who's citing the argument, and how it's landing.",
  },
  {
    title: "Safety Alerts",
    agent: "Agent 7",
    blurb:
      "Real-time escalation of credible threats across every surface, instead of waiting for the weekly digest.",
  },
];

function RoadmapCard({ module: m }: { module: RoadmapModule }) {
  const inner = (
    <>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          {m.agent}
        </span>
        <span
          className={`ml-auto rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
            m.to
              ? "bg-brand-indigo/10 text-brand-indigo"
              : "bg-black/5 text-ink-muted"
          }`}
        >
          {m.to ? "Preview" : "Planned"}
        </span>
      </div>
      <div className="text-base font-bold tracking-tight text-ink">
        {m.title}
      </div>
      <p className="mt-1 text-[13px] leading-snug text-ink-muted">{m.blurb}</p>
      {m.to && (
        <div className="mt-2 text-[12px] font-semibold text-brand-indigo">
          See the concept →
        </div>
      )}
    </>
  );

  const base =
    "flex flex-col rounded-xl border border-rule bg-surface p-4 transition-colors";
  return m.to ? (
    <Link to={m.to} className={`${base} hover:border-brand-indigo/40 hover:shadow-sm`}>
      {inner}
    </Link>
  ) : (
    <div className={`${base} opacity-75`}>{inner}</div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pulse tiles                                                                 */
/* -------------------------------------------------------------------------- */

function PulseTile({
  to,
  kicker,
  label,
  value,
  sub,
  accent,
  velocity,
  spark,
}: {
  to: string;
  kicker: string;
  label: string;
  value: string;
  sub?: string;
  accent?: "up" | "down";
  velocity?: Headline | null;
  spark?: number[];
}) {
  const dir = direction(velocity?.velocity);
  return (
    <Link
      to={to}
      className="group flex min-w-0 flex-col rounded-xl border border-white/10 bg-terminal-panel p-4 transition-colors hover:border-white/25 hover:bg-terminal-elevated"
    >
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {kicker}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div>
          <div
            className={`font-mono text-2xl font-bold leading-none tabular-nums ${
              accent === "up"
                ? "text-up"
                : accent === "down"
                  ? "text-down"
                  : "text-terminal-ink"
            }`}
          >
            {value}
          </div>
          <div className="mt-1.5 text-[11px] uppercase tracking-wider text-terminal-ink-muted">
            {label}
          </div>
          {velocity ? (
            <div
              className={`mt-1 font-mono text-xs font-semibold tabular-nums ${DIRECTION_CLASS[dir]}`}
            >
              {DIRECTION_ARROW[dir]} {formatHeadlineVelocity(velocity)}
            </div>
          ) : (
            sub && (
              <div className="mt-1 text-[11px] text-terminal-ink-muted/80">
                {sub}
              </div>
            )
          )}
        </div>
        {spark && spark.length > 1 && <Sparkline values={spark} />}
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Cross-signal helpers                                                        */
/* -------------------------------------------------------------------------- */

function CrossSignalTooltip({
  active,
  label,
  payload,
  focusAgenda,
  geoKeys,
  reachLabel,
  uploadsByTs,
}: {
  active?: boolean;
  label?: number;
  payload?: Array<{ dataKey: string; value: number }>;
  focusAgenda: FocusAgenda;
  geoKeys: string[];
  reachLabel: string;
  uploadsByTs: Map<number, { ts: number; reach: number; titles: string[] }>;
}) {
  if (!active || label == null) return null;
  const upload = uploadsByTs.get(label);
  return (
    <div className="rounded-md border border-white/15 bg-terminal-elevated px-3 py-2 text-xs text-terminal-ink shadow-lg">
      <div className="mb-1 font-semibold">
        {shortDate(new Date(label).toISOString())}
      </div>
      {geoKeys.map((g) => {
        const v = payload?.find((p) => p.dataKey === g)?.value;
        if (v == null) return null;
        return (
          <div key={g} className="flex items-center gap-2 tabular-nums">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: GEO_COLOURS[g] }}
            />
            <span className="text-terminal-ink-muted">
              {GEO_LABELS[g] ?? g}
            </span>
            <span className="ml-auto font-semibold">
              {formatAgendaValue(v, focusAgenda)}
            </span>
          </div>
        );
      })}
      {upload && upload.reach > 0 && (
        <div className="flex items-center gap-2 tabular-nums">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: BAR_COLOUR }} />
          <span className="text-terminal-ink-muted">{reachLabel}</span>
          <span className="ml-auto font-semibold">
            {upload.reach.toLocaleString("en-GB")}
          </span>
        </div>
      )}
      {upload && upload.titles.length > 0 && (
        <div className="mt-1 border-t border-white/10 pt-1" style={{ color: UPLOAD_COLOUR }}>
          {upload.titles.length === 1
            ? `▶ ${truncate(upload.titles[0], 40)}`
            : `▶ ${upload.titles.length} Gary uploads`}
        </div>
      )}
    </div>
  );
}

function CutThroughRow({
  spike: s,
  termLabel,
  stats,
}: {
  spike: SalienceSpike;
  termLabel: string;
  stats?: { bySentiment: Record<string, number> } | null;
}) {
  const upload = s.garyUploads[0];
  const sentTotal = stats ? sumCounts(stats.bySentiment) : 0;
  const supportive =
    stats && sentTotal > 0
      ? (100 * (stats.bySentiment["supportive"] ?? 0)) / sentTotal
      : null;
  return (
    <li className="rounded-lg border border-white/10 bg-terminal-panel p-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-xs tabular-nums text-terminal-ink-muted">
          {shortDate(s.bucketDate)}
        </span>
        <Link
          to={`/salience/terms/${s.termId}`}
          className="font-semibold text-terminal-ink hover:underline"
        >
          {termLabel}
        </Link>
        <span className="text-[11px] uppercase tracking-wider text-terminal-ink-muted">
          {s.agenda} spike
        </span>
        {s.zScore != null && (
          <span className="rounded bg-down/15 px-1.5 py-0.5 font-mono text-xs font-semibold text-down">
            z {s.zScore.toFixed(1)}
          </span>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <Link
          to={`/youtube/videos/${upload.video_id}`}
          className="inline-flex max-w-full items-center gap-1 rounded bg-up/15 px-1.5 py-0.5 text-up hover:underline"
          title={upload.title}
        >
          <span aria-hidden>▶</span>
          <span className="truncate">{truncate(upload.title, 56)}</span>
        </Link>
        {supportive != null ? (
          <span className="font-mono tabular-nums text-terminal-ink-muted">
            audience: <span className="font-semibold text-up">{supportive.toFixed(0)}% supportive</span>{" "}
            of {sentTotal.toLocaleString("en-GB")} classified
          </span>
        ) : (
          <span className="text-terminal-ink-muted/70">
            audience response pending classification
          </span>
        )}
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Portal news                                                                 */
/* -------------------------------------------------------------------------- */

interface NewsItem {
  ts: number;
  kind: "upload" | "digest" | "spike" | "influence";
  title: string;
  to: string;
  meta?: string;
}

const NEWS_BADGE: Record<NewsItem["kind"], { label: string; cls: string }> = {
  upload: { label: "▶ upload", cls: "bg-[#aab2ff]/15 text-[#aab2ff]" },
  digest: { label: "✦ digest", cls: "bg-agenda-search/15 text-agenda-search" },
  spike: { label: "⚡ spike", cls: "bg-down/15 text-down" },
  influence: { label: "⇄ lead-lag", cls: "bg-agenda-elite/15 text-agenda-elite" },
};

function buildNewsFeed(
  videos: Video[],
  spikes: SalienceSpike[],
  events: Array<SalienceSpike | SalienceInfluence>,
  termLabels: Record<string, { label: string }>,
): NewsItem[] {
  const items: NewsItem[] = [];

  for (const v of videos.slice(0, 12)) {
    if (!v.publishedAt) continue;
    items.push({
      ts: new Date(v.publishedAt).getTime(),
      kind: "upload",
      title: v.title || v.videoId,
      to: `/youtube/videos/${v.videoId}`,
    });
  }
  for (const v of videos) {
    if (!v.lastDigestAt) continue;
    items.push({
      ts: new Date(v.lastDigestAt).getTime(),
      kind: "digest",
      title: v.title || v.videoId,
      to: `/youtube/videos/${v.videoId}`,
      meta: "digest updated",
    });
  }
  for (const s of spikes) {
    items.push({
      ts: bucketTs(s.bucketDate),
      kind: "spike",
      title: `${termLabels[s.termId]?.label ?? s.termId} — ${s.agenda} attention spike`,
      to: `/salience/terms/${s.termId}`,
      meta: s.nearGaryUpload ? "near a Gary upload" : undefined,
    });
  }
  for (const e of events) {
    if (e.kind !== "influence" || !e.candidate) continue;
    items.push({
      ts: new Date(e.sortDate || e.computedAt).getTime(),
      kind: "influence",
      title: `${termLabels[e.termId]?.label ?? e.termId} — ${e.leaderAgenda} may lead ${e.followerAgenda}`,
      to: `/salience/terms/${e.termId}`,
      meta:
        e.bestLagDays != null ? `${e.bestLagDays}d lag candidate` : undefined,
    });
  }

  return items
    .filter((i) => Number.isFinite(i.ts))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 12);
}

/** Feed dates stay compact ("5 Jun") within the current year; older items
 * carry the year ("5 Jun '25") so a stale digest can't masquerade as recent. */
function newsDate(ts: number): string {
  const iso = new Date(ts).toISOString();
  return new Date(ts).getUTCFullYear() === new Date().getUTCFullYear()
    ? dayMonth(iso)
    : dayMonthYear(iso);
}

function NewsRow({ item }: { item: NewsItem }) {
  const badge = NEWS_BADGE[item.kind];
  return (
    <li>
      <Link
        to={item.to}
        className="group flex items-baseline gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5"
      >
        <span className="w-16 shrink-0 font-mono text-[11px] tabular-nums text-terminal-ink-muted">
          {newsDate(item.ts)}
        </span>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge.cls}`}
        >
          {badge.label}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-terminal-ink group-hover:text-white">
          {item.title}
        </span>
        {item.meta && (
          <span className="hidden shrink-0 text-[11px] text-terminal-ink-muted sm:inline">
            {item.meta}
          </span>
        )}
      </Link>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Module summaries                                                            */
/* -------------------------------------------------------------------------- */

function MiniStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg bg-white/5 px-2 py-2">
      <div className="font-mono text-lg font-bold leading-none tabular-nums text-terminal-ink">
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-terminal-ink-muted">
        {label}
      </div>
    </div>
  );
}

function WatchlistMini({
  terms,
}: {
  terms: Record<string, SalienceOverviewTerm>;
}) {
  const rows = Object.entries(terms)
    .map(([id, t]) => ({ id, label: t.label, headline: termHeadline(t) }))
    .filter((r) => r.headline && (r.headline.value ?? 0) > 0)
    .sort((a, b) => (b.headline!.value ?? 0) - (a.headline!.value ?? 0))
    .slice(0, 5);

  if (rows.length === 0) {
    return (
      <div className="text-sm text-terminal-ink-muted">
        No live signals yet on the watchlist.
      </div>
    );
  }

  return (
    <ul className="space-y-1">
      {rows.map((r) => {
        const dir = direction(r.headline!.velocity);
        return (
          <li key={r.id}>
            <Link
              to={`/salience/terms/${r.id}`}
              className="flex items-baseline justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5"
            >
              <span className="truncate text-sm text-terminal-ink">
                {r.label}
              </span>
              <span className="flex shrink-0 items-baseline gap-2 font-mono text-xs tabular-nums">
                <span className="font-semibold text-terminal-ink">
                  {formatHeadlineValue(r.headline!)}
                </span>
                <span className={`font-semibold ${DIRECTION_CLASS[dir]}`}>
                  {DIRECTION_ARROW[dir]} {formatHeadlineVelocity(r.headline!)}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function ModuleLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="shrink-0 rounded border border-white/10 bg-terminal-panel px-2 py-1 text-[11px] font-semibold text-terminal-ink-muted transition-colors hover:text-terminal-ink"
    >
      {label} →
    </Link>
  );
}

function FooterLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="text-[#aab2ff] hover:underline">
      {label} →
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function sumCounts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("en-GB");
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
